import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, type ActionMenuItem, SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import type { DineOutletAdmin } from 'api/types';
import DineOpsOutletFormModal from 'components/modals/modules/dineops-module/DineOpsOutletFormModal';
import DineOpsOutletStationsModal from 'components/modals/modules/dineops-module/DineOpsOutletStationsModal';
import DineOpsOutletMealPeriodsModal from 'components/modals/modules/dineops-module/DineOpsOutletMealPeriodsModal';
import DineOpsOutletTaxProfileModal from 'components/modals/modules/dineops-module/DineOpsOutletTaxProfileModal';
import {
  buffetSummary, stationsMissing, takeOutletSectionRequest, taxMissing, taxSummary, type OutletSection,
} from './outletWorkspace';

/**
 * SETUP-006 — Outlets (multi-outlet, spec 2.36 §7.5).
 *
 * The menu and the kitchen stations are one master for the tenant (DO-312). Each
 * outlet keeps its own tables, price lists, numbering and GSTIN, says which
 * kitchen stations it may send to (row action Kitchen Stations) and lists the
 * dishes it sells on Outlet Items. Who works where is set on Outlet Access.
 * Each outlet links to an organisation unit (usually the F&B department), chosen
 * on the form; units themselves are made in Organisation → Business Units.
 */

interface Filters { status?: string; menu?: string; departments?: string; property?: string; unit?: string }

const DineOpsOutletsScreen: React.FC<{ allowedActions?: string[] }> = ({ allowedActions = [] }) => {
  const [rows, setRows] = useState<DineOutletAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState<Filters>({});

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selected, setSelected] = useState<DineOutletAdmin | null>(null);
  const [stationsFor, setStationsFor] = useState<DineOutletAdmin | null>(null);
  const [mealPeriodsFor, setMealPeriodsFor] = useState<DineOutletAdmin | null>(null);
  const [taxProfileFor, setTaxProfileFor] = useState<DineOutletAdmin | null>(null);
  const [openSection, setOpenSection] = useState<OutletSection>('general');

  const canAdd = allowedActions.includes('add') || allowedActions.includes('create');
  const canEdit = allowedActions.includes('edit') || allowedActions.includes('update');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setRows(await dineOpsService.getOutlets(true));
    } catch (err: any) {
      setLoadError(err?.response?.data?.message ?? 'The outlets could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rows.filter((r) => {
      if (q && !`${r.outletCode} ${r.outletName} ${r.propertyName ?? ''} ${r.gstin ?? ''}`.toLowerCase().includes(q)) return false;
      if (filters.status === 'active' && !r.isActive) return false;
      if (filters.status === 'inactive' && r.isActive) return false;
      if (filters.menu === 'empty' && r.itemCount > 0) return false;
      if (filters.menu === 'has' && r.itemCount === 0) return false;
      if (filters.departments === 'none' && r.stationCount > 0) return false;
      if (filters.departments === 'some' && r.stationCount === 0) return false;
      if (filters.property && (r.propertyName ?? '') !== filters.property) return false;
      if (filters.unit === 'none' && r.businessUnitId) return false;
      if (filters.unit === 'linked' && !r.businessUnitId) return false;
      return true;
    });
  }, [rows, searchQuery, filters]);

  const propertyOptions = useMemo(
    () => [...new Set(rows.map((r) => r.propertyName).filter(Boolean) as string[])].map((p) => ({ value: p, label: p })),
    [rows],
  );

  const handleAdd = useCallback(() => { setFormMode('create'); setSelected(null); setFormOpen(true); }, []);
  const handleEdit = useCallback((r: DineOutletAdmin) => { setFormMode('edit'); setSelected(r); setOpenSection('general'); setFormOpen(true); }, []);

  /**
   * The outlet window at one section. Someone without edit opens the read-only windows instead, as before
   * (the window's General and Billing are a form).
   */
  const openAt = useCallback((r: DineOutletAdmin, s: OutletSection) => {
    if (canEdit) { setFormMode('edit'); setSelected(r); setOpenSection(s); setFormOpen(true); return; }
    if (s === 'tax') setTaxProfileFor(r);
    else if (s === 'stations') setStationsFor(r);
    else if (s === 'meals') setMealPeriodsFor(r);
  }, [canEdit]);

  // A Go from the Setup Checklist: open that outlet at that section once the list is in.
  useEffect(() => {
    if (loading || rows.length === 0) return;
    const request = takeOutletSectionRequest();
    const target = request && rows.find((r) => r.id === request.outletId);
    if (request && target) openAt(target, request.section);
  }, [loading, rows, openAt]);

  const handleExport = useCallback(() => {
    if (filtered.length === 0) { notificationService.toast.warning('No data to export'); return; }
    const headers = ['Code', 'Outlet', 'Property', 'Business Unit', 'GSTIN', 'Sections', 'Tables', 'Dishes sold', 'Kitchen Stations', 'Status'];
    const lines = filtered.map((r) => [r.outletCode, r.outletName, r.propertyName, r.businessUnitName, r.gstin, r.sectionCount, r.tableCount, r.itemCount, r.stationCount, r.isActive ? 'Active' : 'Inactive']
      .map((v) => { const s = String(v ?? ''); return s.includes(',') ? `"${s}"` : s; }).join(','));
    const blob = new Blob([[headers.join(','), ...lines].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `outlets_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }, [filtered]);

  const columns = useMemo((): SyutGridColumn<DineOutletAdmin>[] => [
    { id: 'outletCode', header: 'Code', accessorKey: 'outletCode', enableSorting: true, size: 100 },
    { id: 'outletName', header: 'Outlet', accessorKey: 'outletName', enableSorting: true, size: 220 },
    { id: 'propertyName', header: 'Property', accessorKey: 'propertyName', enableSorting: true, size: 180,
      cell: ({ row }) => row.original.propertyName ?? <span style={{ color: 'var(--text-muted)' }}>standalone</span> },
    { id: 'businessUnitName', header: 'Business Unit', accessorKey: 'businessUnitName', enableSorting: true, size: 180,
      cell: ({ row }) => row.original.businessUnitName ?? <span style={{ color: 'var(--text-muted)' }}>not linked</span> },
    { id: 'gstin', header: 'GSTIN', accessorKey: 'gstin', enableSorting: false, size: 170, cell: ({ row }) => row.original.gstin ?? '—' },
    // A section and its tables belong to one outlet; they are edited on Sections / Tables with the outlet in the header.
    { id: 'outletType', header: 'Service', accessorKey: 'outletType', enableSorting: true, size: 120,
      cell: ({ row }) => (row.original.isRoomService ? 'Room service' : 'Tables') },
    { id: 'sectionCount', header: 'Sections', accessorKey: 'sectionCount', enableSorting: true, size: 100 },
    { id: 'tableCount', header: 'Tables', accessorKey: 'tableCount', enableSorting: true, size: 90 },
    { id: 'itemCount', header: 'Dishes sold', accessorKey: 'itemCount', enableSorting: true, size: 110 },
    { id: 'stationCount', header: 'Kitchen Stations', accessorKey: 'stationCount', enableSorting: true, size: 130,
      cell: ({ row }) => (
        <button type="button" className={`outlet-status-cell${stationsMissing(row.original) ? ' warn' : ''}`}
          onClick={() => openAt(row.original, 'stations')} title="Kitchen stations">{row.original.stationCount}</button>
      ) },
    { id: 'tax', header: 'Tax', accessorKey: 'taxRateCode', enableSorting: false, size: 190,
      cell: ({ row }) => {
        const summary = taxSummary(row.original);
        return (
          <button type="button" className={`outlet-status-cell${taxMissing(row.original) ? ' warn' : ''}`}
            onClick={() => openAt(row.original, 'tax')} title="Tax profile">
            {summary ?? (row.original.taxCalculationEnabled ? 'Not set' : 'Tax off')}
          </button>
        );
      } },
    { id: 'buffet', header: 'Buffet', accessorKey: 'buffetMeals', enableSorting: false, size: 150,
      cell: ({ row }) => (
        <button type="button" className="outlet-status-cell" onClick={() => openAt(row.original, 'meals')} title="Meal periods">
          {buffetSummary(row.original) ?? '—'}
        </button>
      ) },
    { id: 'isActive', header: 'Status', accessorKey: 'isActive', enableSorting: true, size: 100,
      cell: ({ row }) => (row.original.isActive ? 'Active' : 'Inactive') },
  ], [openAt]);

  const getRowActions = useCallback((row: DineOutletAdmin): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    // Open the outlet window; the other entries open it (or, without edit, the read-only window) at that part.
    if (canEdit) items.push({ id: 'edit', label: 'Open outlet', icon: <IconSystem.edit size={16} />, onClick: () => handleEdit(row) });
    items.push({ id: 'departments', label: 'Kitchen Stations', icon: <IconSystem.settings size={16} />, onClick: () => openAt(row, 'stations') });
    items.push({ id: 'meal-periods', label: 'Meal Periods', icon: <IconSystem.calendar size={16} />, onClick: () => openAt(row, 'meals') });
    // Readable by anyone on the screen; changing it needs 'tax' (checked again by the server).
    items.push({ id: 'tax-profile', label: 'Tax Profile', icon: <IconSystem.percent size={16} />, onClick: () => openAt(row, 'tax') });
    return items;
  }, [canEdit, handleEdit, openAt]);

  const actionButtons: ActionButton[] = useMemo(() => {
    const b: ActionButton[] = [];
    if (canAdd) b.push({ id: 'add', icon: <IconSystem.add size={18} />, label: '+', onClick: handleAdd, tooltip: 'Add Outlet', variant: 'icon' });
    b.push({ id: 'refresh', icon: <IconSystem.refresh size={18} />, label: '', onClick: () => void load(), tooltip: 'Refresh data', variant: 'icon' });
    b.push({ id: 'export', icon: <IconSystem.download size={18} />, label: '', onClick: handleExport, tooltip: 'Export to CSV', variant: 'icon' });
    return b;
  }, [canAdd, handleAdd, load, handleExport]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Outlets"
        description="One menu for all outlets. Each outlet has its own tables, numbering and GSTIN, its business unit, and the kitchen stations it may use"
        icon={<IconSystem.home size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search code, outlet, property or GSTIN' }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, tooltip: 'Toggle filters' }}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
            <SyutGrid
              data={filtered}
              columns={columns}
              enableSorting
              enablePagination={true}
              defaultPageSize={25}
              pageSizeOptions={[10, 25, 50, 100]}
              frozenRightColumns={['actions']}
              isLoading={loading}
              rowActions={getRowActions}
              emptyMessage={loadError ?? 'No records found.'}
            />
          </div>
        </div>

        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters"><IconSystem.x size={16} /></button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect label="Status" options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
                value={filters.status ?? ''} onChange={(v) => setFilters((p) => ({ ...p, status: (v as string) || undefined }))}
                placeholder="All statuses" size="sm" isClearable />
            </div>
            <div className="filter-section">
              <SyutSelect label="Dishes sold" options={[{ value: 'has', label: 'Sells dishes' }, { value: 'empty', label: 'Sells nothing yet' }]}
                value={filters.menu ?? ''} onChange={(v) => setFilters((p) => ({ ...p, menu: (v as string) || undefined }))}
                placeholder="Any" size="sm" isClearable />
            </div>
            <div className="filter-section">
              <SyutSelect label="Kitchen Stations" options={[{ value: 'some', label: 'Uses kitchen stations' }, { value: 'none', label: 'No kitchen station yet' }]}
                value={filters.departments ?? ''} onChange={(v) => setFilters((p) => ({ ...p, departments: (v as string) || undefined }))}
                placeholder="Any" size="sm" isClearable />
            </div>
            <div className="filter-section">
              <SyutSelect label="Business Unit" options={[{ value: 'linked', label: 'Linked' }, { value: 'none', label: 'Not linked yet' }]}
                value={filters.unit ?? ''} onChange={(v) => setFilters((p) => ({ ...p, unit: (v as string) || undefined }))}
                placeholder="Any" size="sm" isClearable />
            </div>
            <div className="filter-section">
              <SyutSelect label="Property" options={propertyOptions}
                value={filters.property ?? ''} onChange={(v) => setFilters((p) => ({ ...p, property: (v as string) || undefined }))}
                placeholder="All properties" size="sm" isClearable />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={() => setFilters({})}>Clear</button>
            <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Apply</button>
          </div>
        </div>
      </div>

      <DineOpsOutletFormModal isOpen={formOpen} mode={formMode} outlets={rows}
        record={selected ? rows.find((r) => r.id === selected.id) ?? selected : null}
        initialSection={openSection} canEdit={canEdit}
        onClose={() => setFormOpen(false)} onSaved={() => void load()} />
      <DineOpsOutletStationsModal isOpen={!!stationsFor} outlet={stationsFor} canEdit={canEdit}
        onClose={() => setStationsFor(null)} onSaved={() => void load()} />
      <DineOpsOutletMealPeriodsModal isOpen={!!mealPeriodsFor} outlet={mealPeriodsFor} canEdit={canEdit}
        onClose={() => setMealPeriodsFor(null)} onSaved={() => void load()} />
      <DineOpsOutletTaxProfileModal isOpen={!!taxProfileFor} outlet={taxProfileFor} onClose={() => setTaxProfileFor(null)} />
    </div>
  );
};

export default DineOpsOutletsScreen;
